import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
    FaTimes,
    FaEnvelope,
    FaLock,
    FaUser,
    FaArrowRight,
    FaShieldAlt,
    FaCheckCircle,
} from "react-icons/fa";

export default function LoginModal({ isOpen, onClose, setUser }) {
    const [mode, setMode] = useState("login");
    const [errors, setErrors] = useState({});
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();

    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
    });

    if (!isOpen) return null;

    // HANDLE INPUT CHANGE
    const handleChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        });

        setErrors({
            ...errors,
            [e.target.name]: "",
            general: "",
        });

        setSuccess("");
    };

    // VALIDATION
    const validate = () => {
        let newErrors = {};

        if (mode === "register" && !form.name.trim()) {
            newErrors.name = "Full name is required";
        }

        if (!form.email.trim()) {
            newErrors.email = "Email is required";
        }

        if (mode !== "forgot" && !form.password.trim()) {
            newErrors.password = "Password is required";
        }

        return newErrors;
    };

    // SUBMIT
    const handleSubmit = async (e) => {
        e.preventDefault();

        setErrors({});
        setSuccess("");

        const validationErrors = validate();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);

        try {
            // FORGOT PASSWORD
            if (mode === "forgot") {
                await axios.post(
                    "https://cheapdubaihotels.com/api/forgot-password",
                    {
                        email: form.email,
                    }
                );

                setSuccess(
                    "If an account exists with this email, a password reset link has been sent."
                );

                setLoading(false);
                return;
            }

            // REGISTER
            if (mode === "register") {
                await axios.post(
                    "https://cheapdubaihotels.com/api/register",
                    {
                        name: form.name,
                        email: form.email,
                        password: form.password,
                        password_confirmation: form.password,
                    }
                );

                alert("User registered successfully");

                setForm({
                    name: "",
                    email: "",
                    password: "",
                });

                setMode("login");
                setLoading(false);

                return;
            }

            // LOGIN
            if (mode === "login") {
                const res = await axios.post(
                    "https://cheapdubaihotels.com/api/login",
                    {
                        email: form.email,
                        password: form.password,
                    }
                );

                const userData = res.data.user;

                // Save user
                localStorage.setItem(
                    "user",
                    JSON.stringify(userData)
                );

                setUser(userData);

                alert("Login successful");

                setLoading(false);

                // Close modal
                onClose();

                // Redirect
                navigate("/profile");
            }
        } catch (err) {
            console.error(err);

            setLoading(false);

            if (mode === "forgot") {
                setErrors({
                    general:
                        err.response?.data?.message ||
                        "Unable to send the reset link. Please try again later.",
                });
            } else {
                setErrors({
                    general:
                        err.response?.data?.message ||
                        "Authentication failed. Please check your details.",
                });
            }
        }
    };

    // SWITCH TO LOGIN
    const goToLogin = () => {
        setMode("login");
        setErrors({});
        setSuccess("");

        setForm({
            name: "",
            email: form.email,
            password: "",
        });
    };

    // SWITCH TO REGISTER
    const goToRegister = () => {
        setMode("register");
        setErrors({});
        setSuccess("");
    };

    // SWITCH TO FORGOT PASSWORD
    const goToForgotPassword = () => {
        setMode("forgot");
        setErrors({});
        setSuccess("");

        setForm({
            name: "",
            email: form.email,
            password: "",
        });
    };

    const inputClass = `
w - full
rounded - xl
border
border - [#DCE8EB]
bg - [#F5F9FA]
py - 3.5
pl - 11
pr - 4
text - sm
font - medium
text - [#173F4A]
outline - none
transition - all
duration - 300
placeholder: text - [#91A2A9]
focus: border - [#0B6E8E]
focus: bg - white
focus: ring - 4
focus: ring - [#0B6E8E] / [0.10]
    `;

    return (
        <div
            className="
                fixed inset-0 z-50
                flex items-center justify-center
                overflow-y-auto
                bg-[#102F3B]/75
                px-4 py-6
                backdrop-blur-md
            "
            onClick={onClose}
        >

            {/* MODAL */}
            <div
                className="
                    relative
                    my-auto
                    w-full
                    max-w-[470px]
                    overflow-hidden
                    rounded-[26px]
                    border
                    border-[#F7B928]/30
                    bg-white
                    shadow-[0_30px_100px_rgba(11,110,142,0.22)]
                "
                onClick={(e) => e.stopPropagation()}
            >

                {/* TOP ACCENT */}
                <div className="h-1.5 w-full bg-gradient-to-r from-[#FF5A1F] via-[#FF5A1F] to-[#F7B928]" />

                {/* DECORATIVE BACKGROUND */}
                <div
                    className="
                        pointer-events-none
                        absolute
                        -right-24 -top-24
                        h-56 w-56
                        rounded-full
                        bg-[#FF5A1F]/[0.06]
                        blur-3xl
                    "
                />

                <div
                    className="
                        pointer-events-none
                        absolute
                        -bottom-24 -left-24
                        h-56 w-56
                        rounded-full
                        bg-[#F7B928]/[0.14]
                        blur-3xl
                    "
                />

                {/* CLOSE BUTTON */}
                <button
                    onClick={onClose}
                    type="button"
                    aria-label="Close"
                    className="
                        absolute right-5 top-5 z-30
                        flex h-10 w-10
                        items-center justify-center
                        rounded-full
                        border border-[#DCE8EB]
                        bg-white
                        text-[#0B6E8E]
                        shadow-sm
                        transition-all duration-300
                        hover:border-[#FF5A1F]/30
                        hover:bg-[#FF5A1F]
                        hover:text-white
                        hover:rotate-90
                    "
                >
                    <FaTimes className="text-sm" />
                </button>

                {/* MAIN CONTENT */}
                <div className="relative z-10 p-6 sm:p-9">

                    {/* BRAND / ICON */}
                    <div className="text-center">

                        <div
                            className="
                                relative mx-auto
                                flex h-16 w-16
                                items-center justify-center
                                rounded-[20px]
                                border border-[#FF5A1F]/15
                                bg-[#FF5A1F]/[0.06]
                                text-[#FF5A1F]
                                shadow-[0_8px_25px_rgba(255,90,31,0.10)]
                                transition-transform duration-300
                                hover:-translate-y-1
                            "
                        >
                            <span
                                className="
                                    absolute -right-1 -top-1
                                    h-3 w-3
                                    rounded-full
                                    bg-[#F7B928]
                                    ring-4 ring-white
                                "
                            />

                            {mode === "register" ? (
                                <FaUser className="text-xl" />
                            ) : mode === "forgot" ? (
                                <FaEnvelope className="text-xl" />
                            ) : (
                                <FaLock className="text-xl" />
                            )}
                        </div>

                        {/* EYEBROW */}
                        <div
                            className="
                                mx-auto mt-5
                                inline-flex items-center gap-2
                                text-[10px]
                                font-extrabold
                                uppercase
                                tracking-[0.2em]
                                text-[#FF5A1F]
                            "
                        >
                            <span className="h-px w-6 bg-[#F7B928]" />

                            {mode === "register"
                                ? "Join Dubai Marina"
                                : mode === "forgot"
                                    ? "Account Recovery"
                                    : "Welcome Back"}

                            <span className="h-px w-6 bg-[#F7B928]" />
                        </div>

                        {/* TITLE */}
                        <h2
                            className="
                                mt-4
                                text-3xl
                                font-extrabold
                                tracking-tight
                                text-[#0B6E8E]
                                sm:text-[34px]
                            "
                        >
                            {mode === "register"
                                ? "Create Your Account"
                                : mode === "forgot"
                                    ? "Reset Your Password"
                                    : "Sign In"}
                        </h2>

                        {/* SUBTITLE */}
                        <p
                            className="
                                mx-auto mt-2
                                max-w-sm
                                text-sm
                                leading-6
                                text-[#71838B]
                            "
                        >
                            {mode === "register"
                                ? "Create your account and make managing your Dubai stays easier."
                                : mode === "forgot"
                                    ? "Enter your email and we'll send you a secure password reset link."
                                    : "Sign in to manage your hotel bookings and reservations."}
                        </p>

                    </div>

                    {/* GENERAL ERROR */}
                    {errors.general && (
                        <div
                            className="
                                mt-6 flex items-start gap-3
                                rounded-xl
                                border border-red-200
                                bg-red-50
                                px-4 py-3.5
                                text-sm leading-5
                                text-red-600
                            "
                        >
                            <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-red-500" />

                            <span>{errors.general}</span>
                        </div>
                    )}

                    {/* SUCCESS MESSAGE */}
                    {success && (
                        <div
                            className="
                                mt-6 flex items-start gap-3
                                rounded-xl
                                border border-[#0B6E8E]/20
                                bg-[#0B6E8E]/[0.06]
                                px-4 py-3.5
                                text-sm leading-5
                                text-[#0B6E8E]
                            "
                        >
                            <FaCheckCircle className="mt-0.5 shrink-0" />

                            <span>{success}</span>
                        </div>
                    )}

                    {/* FORM */}
                    <form
                        onSubmit={handleSubmit}
                        className="mt-7 space-y-5"
                    >

                        {/* NAME */}
                        {mode === "register" && (
                            <div>
                                <label
                                    htmlFor="name"
                                    className="
            mb-2 block
            text-[11px]
            font-extrabold
            uppercase
            tracking-[0.12em]
            text-[#0B6E8E]
        "
                                >
                                    Full Name
                                </label>

                                <div className="relative">
                                    <FaUser
                                        className="
                absolute left-4 top-1/2
                -translate-y-1/2
                text-sm
                text-[#FF5A1F]/70
            "
                                    />

                                    <input
                                        id="name"
                                        name="name"
                                        value={form.name}
                                        placeholder="Enter your full name"
                                        className="
                w-full rounded-xl
                border border-gray-200
                bg-gray-50
                py-3.5 pl-12 pr-4
                text-sm text-gray-800
                placeholder:text-gray-400
                outline-none
                transition-all duration-300
                hover:border-[#0B6E8E]/50
                focus:border-[#0B6E8E]
                focus:bg-white
                focus:ring-4 focus:ring-[#0B6E8E]/10
            "
                                    />
                                </div>

                                {errors.name && (
                                    <p className="mt-1.5 text-xs text-red-600">
                                        {errors.name}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* EMAIL */}
                        <div>
                            <label
                                htmlFor="email"
                                className="
            mb-2 block
            text-[11px]
            font-extrabold
            uppercase
            tracking-[0.12em]
            text-[#0B6E8E]
        "
                            >
                                Email Address
                            </label>

                            <div className="relative">
                                <FaEnvelope
                                    className="
                absolute left-4 top-1/2
                -translate-y-1/2
                text-sm
                text-[#FF5A1F]/70
            "
                                />

                                <input
                                    id="email"
                                    type="email"
                                    name="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    placeholder="Enter your email"
                                    className="
                w-full rounded-xl
                border border-gray-200
                bg-gray-50
                py-3.5 pl-12 pr-4
                text-sm text-gray-800
                placeholder:text-gray-400
                outline-none
                transition-all duration-300
                hover:border-[#0B6E8E]/50
                focus:border-[#0B6E8E]
                focus:bg-white
                focus:ring-4 focus:ring-[#0B6E8E]/10
            "
                                />
                            </div>

                            {errors.email && (
                                <p className="mt-1.5 text-xs text-red-600">
                                    {errors.email}
                                </p>
                            )}
                        </div>

                        {/* PASSWORD */}
                        {mode !== "forgot" && (
                            <div>

                                <label
                                    htmlFor="password"
                                    className="
                                        mb-2 block
                                        text-[11px]
                                        font-extrabold
                                        uppercase
                                        tracking-[0.12em]
                                        text-[#0B6E8E]
                                    "
                                >
                                    Password
                                </label>

                                <div className="relative">
                                    <FaLock
                                        className="
            absolute left-4 top-1/2
            -translate-y-1/2
            text-sm
            text-[#FF5A1F]/70
        "
                                    />

                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        value={form.password}
                                        placeholder="Enter your password"
                                        onChange={handleChange}
                                        className="
            w-full rounded-xl
            border border-gray-200
            bg-gray-50
            py-3.5 pl-12 pr-4
            text-sm text-gray-800
            placeholder:text-gray-400
            outline-none
            transition-all duration-300
            hover:border-[#0B6E8E]/50
            focus:border-[#0B6E8E]
            focus:bg-white
            focus:ring-4 focus:ring-[#0B6E8E]/10
        "
                                    />
                                </div>

                                {errors.password && (
                                    <p className="mt-1.5 text-xs text-red-600">
                                        {errors.password}
                                    </p>
                                )}

                                {/* FORGOT PASSWORD */}
                                {mode === "login" && (
                                    <div className="mt-3 flex justify-end">

                                        <button
                                            type="button"
                                            onClick={goToForgotPassword}
                                            className="
                                                text-xs
                                                font-bold
                                                text-[#0B6E8E]
                                                transition-all duration-300
                                                hover:text-[#FF5A1F]
                                                hover:underline
                                            "
                                        >
                                            Forgot Password?
                                        </button>

                                    </div>
                                )}

                            </div>
                        )}

                        {/* SUBMIT BUTTON */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="
                                group relative
                                flex w-full
                                items-center justify-center gap-3
                                overflow-hidden
                                rounded-xl
                                bg-[#FF5A1F]
                                px-5 py-4
                                text-sm font-extrabold
                                text-white
                                shadow-[0_12px_30px_rgba(255,90,31,0.20)]
                                transition-all duration-300
                                hover:-translate-y-0.5
                                hover:bg-[#E94B12]
                                hover:shadow-[0_16px_35px_rgba(255,90,31,0.28)]
                                focus:outline-none
                                focus:ring-4
                                focus:ring-[#FF5A1F]/20
                                disabled:cursor-not-allowed
                                disabled:opacity-60
                                disabled:hover:translate-y-0
                            "
                        >

                            {/* YELLOW HOVER LINE */}
                            <span
                                className="
                                    absolute bottom-0 left-0
                                    h-1 w-0
                                    bg-[#F7B928]
                                    transition-all duration-500
                                    group-hover:w-full
                                "
                            />

                            {loading ? (
                                <>
                                    <span
                                        className="
                                            h-5 w-5
                                            animate-spin
                                            rounded-full
                                            border-2
                                            border-white/30
                                            border-t-white
                                        "
                                    />

                                    Please wait...
                                </>
                            ) : (
                                <>
                                    {mode === "register"
                                        ? "Create Account"
                                        : mode === "forgot"
                                            ? "Send Reset Link"
                                            : "Sign In"}

                                    <FaArrowRight
                                        className="
                                            text-xs
                                            transition-transform
                                            duration-300
                                            group-hover:translate-x-1
                                        "
                                    />
                                </>
                            )}

                        </button>

                    </form>

                    {/* BOTTOM NAVIGATION */}
                    <div className="mt-6 text-center text-sm text-[#71838B]">

                        {mode === "login" && (
                            <>
                                Don't have an account?{" "}

                                <button
                                    type="button"
                                    onClick={goToRegister}
                                    className="
                                        font-extrabold
                                        text-[#FF5A1F]
                                        transition-colors duration-300
                                        hover:text-[#0B6E8E]
                                    "
                                >
                                    Create account
                                </button>
                            </>
                        )}

                        {mode === "register" && (
                            <>
                                Already have an account?{" "}

                                <button
                                    type="button"
                                    onClick={goToLogin}
                                    className="
                                        font-extrabold
                                        text-[#FF5A1F]
                                        transition-colors duration-300
                                        hover:text-[#0B6E8E]
                                    "
                                >
                                    Sign in
                                </button>
                            </>
                        )}

                        {mode === "forgot" && (
                            <button
                                type="button"
                                onClick={goToLogin}
                                className="
                                    inline-flex items-center gap-2
                                    font-extrabold
                                    text-[#FF5A1F]
                                    transition-colors duration-300
                                    hover:text-[#0B6E8E]
                                "
                            >
                                ← Back to login
                            </button>
                        )}

                    </div>

                    {/* SECURITY NOTE */}
                    <div
                        className="
                            mt-7 flex items-center justify-center gap-2
                            border-t border-[#DCE8EB]
                            pt-5
                            text-[10px]
                            font-extrabold
                            uppercase
                            tracking-[0.15em]
                            text-[#82949C]
                        "
                    >
                        <FaShieldAlt className="text-[#F7B928]" />

                        Secure & Protected
                    </div>

                </div>

            </div>

        </div>
    );
}
